import React, { createContext, useState, useContext, useCallback } from 'react';
import SimpleToast from '../components/ui/SimpleToast';

// Create the context
const ToastContext = createContext();

/**
 * Toast provider component that manages toast notifications
 */
export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  // Add a toast notification
  const addToast = useCallback((message, type = 'success', duration = 3000) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    setToasts(prev => [...prev, { id, message, type, duration }]);
    return id;
  }, []);

  // Remove a toast notification
  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  // Convenience methods for different toast types
  const showSuccess = useCallback((message, duration = 3000) => 
    addToast(message, 'success', duration), [addToast]);
  
  const showError = useCallback((message, duration = 3000) => 
    addToast(message, 'error', duration), [addToast]);
  
  const showInfo = useCallback((message, duration = 3000) => 
    addToast(message, 'info', duration), [addToast]);
  
  const showWarning = useCallback((message, duration = 3000) => 
    addToast(message, 'warning', duration), [addToast]);

  return (
    <ToastContext.Provider value={{ 
      showSuccess, 
      showError, 
      showInfo, 
      showWarning 
    }}>
      {children}
      
      {/* Render all active toasts */}
      {toasts.map(toast => (
        <SimpleToast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          onClose={() => removeToast(toast.id)}
        />
      ))}
    </ToastContext.Provider>
  );
};

/**
 * Custom hook to use the toast context
 */
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export default ToastContext;
